<template>
  <span class="bdx-preview-wrapper">
    <span
      :id="triggerId"
      class="bdx-preview-trigger"
      title="Preview"
      role="button"
      tabindex="0"
      @click.stop="openPopover($event)"
    >
      <BaseIcon icon="eye" tag="i" :font-scale="0.9" />
    </span>

    <!-- Panel is always in DOM (v-show), moved to <body> on mount to escape stacking contexts -->
    <div
      v-show="isVisible"
      ref="panelRef"
      class="bdx-pv-panel"
      :style="panelStyle"
      @mousedown="bringToFront"
    >
      <div class="bdx-pv-drag-handle px-3" @mousedown="onHeaderMousedown">
        <div class="d-flex align-items-center" style="gap: 6px; pointer-events: none; min-width: 0;">
          <span class="bdx-pv-grip" title="Drag to move">⠿</span>
          <span class="bdx-pv-title" @mousedown.stop>Preview &mdash; {{ item.umr || '' }}</span>
        </div>
        <div @mousedown.stop>
          <button type="button" class="bdx-pv-close-btn" aria-label="Close" @click.stop="closePopover">
            &times;
          </button>
        </div>
      </div>

      <div class="pt-2 pb-3 px-3">
        <div v-if="isFetching" class="d-flex align-items-center small py-2">
          <b-spinner small class="mr-2" />
          Loading...
        </div>

        <div v-else-if="!shownRows.length" class="text-muted small py-2">
          No preview data available.
        </div>

        <div v-else>
          <div class="bdx-pv-scroll">
            <table class="table table-sm table-bordered mb-0 small bdx-pv-table">
              <thead>
                <tr>
                  <th v-for="col in columnDefs" :key="col.key" class="bdx-pv-th">{{ col.label }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in shownRows" :key="idx">
                  <td v-for="col in columnDefs" :key="col.key">
                    {{ row[col.key] != null && row[col.key] !== '' ? row[col.key] : '—' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="small text-muted mt-1">
            {{ shownRows.length }} record{{ shownRows.length === 1 ? '' : 's' }} shown
          </div>
        </div>
      </div>
    </div>
  </span>
</template>

<script>
import {
  defineComponent,
  ref,
  computed,
  onMounted,
  onUnmounted
} from '@vue/composition-api'
import { uniqueId } from 'lodash-es'

import { BaseIcon } from '@components/base'

const previewCache = new Map()

let globalZIndex = 9000
let openCount = 0

const normalize = (k) => String(k).toLowerCase().replace(/[\s_#]/g, '')

export default defineComponent({
  name: 'BDXPreviewPopover',
  components: { BaseIcon },
  props: {
    item: {
      type: Object,
      required: true
    },
    fetchFn: {
      type: Function,
      default: null
    },
    columns: {
      type: Array,
      default: null
    },
    latestOnly: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { refs }) {
    const triggerId = ref(uniqueId('bdx-pv-trigger-'))
    const panelRef = ref(null)
    const isVisible = ref(false)
    const isFetching = ref(false)
    const displayRows = ref([])
    const pos = ref({ top: 200, left: 400 })
    const zIndex = ref(globalZIndex)

    const panelStyle = computed(() => ({
      top: `${pos.value.top}px`,
      left: `${pos.value.left}px`,
      zIndex: zIndex.value
    }))

    const bringToFront = () => {
      globalZIndex += 1
      zIndex.value = globalZIndex
    }

    const columnDefs = computed(() => {
      if (!displayRows.value.length) return []
      const actualKeys = Object.keys(displayRows.value[0])

      if (!props.columns || !props.columns.length) {
        return actualKeys.map((k) => ({ key: k, label: k.replace(/_/g, ' ') }))
      }

      return props.columns
        .map(({ key, label }) => {
          const n = normalize(key)
          const match = actualKeys.find((k) => normalize(k) === n)
          return match ? { key: match, label } : null
        })
        .filter(Boolean)
    })

    const shownRows = computed(() => {
      if (!displayRows.value.length) return []
      if (props.latestOnly) return [displayRows.value[displayRows.value.length - 1]]
      return displayRows.value
    })

    const cacheKey = () =>
      props.item.umr_rc_cc_sn || props.item.umr || String(props.item.id) || ''

    const loadData = async () => {
      const key = cacheKey()
      if (previewCache.has(key)) {
        displayRows.value = previewCache.get(key)
        return
      }
      if (!props.fetchFn) return
      isFetching.value = true
      displayRows.value = []
      try {
        const rows = await props.fetchFn(props.item)
        const result = Array.isArray(rows) ? rows : []
        if (result.length) previewCache.set(key, result)
        displayRows.value = result
      } catch (_) {
        displayRows.value = []
      } finally {
        isFetching.value = false
      }
    }

    // ── Drag ───────────────────────────────────────────────────────
    let dragStartMouse = { x: 0, y: 0 }
    let dragStartPos = { top: 0, left: 0 }

    const onDragMove = (e) => {
      const dx = e.clientX - dragStartMouse.x
      const dy = e.clientY - dragStartMouse.y
      pos.value = {
        top: Math.max(0, dragStartPos.top + dy),
        left: Math.max(0, dragStartPos.left + dx)
      }
    }

    const onDragEnd = () => {
      document.body.style.userSelect = ''
      document.removeEventListener('mousemove', onDragMove)
      document.removeEventListener('mouseup', onDragEnd)
    }

    const onHeaderMousedown = (e) => {
      if (e.target.closest('button')) return
      e.preventDefault()
      dragStartMouse = { x: e.clientX, y: e.clientY }
      dragStartPos = { top: pos.value.top, left: pos.value.left }
      document.body.style.userSelect = 'none'
      document.addEventListener('mousemove', onDragMove)
      document.addEventListener('mouseup', onDragEnd)
    }

    const openPopover = (e) => {
      const PANEL_W = 720
      const PANEL_H = 320

      const rect = e.currentTarget.getBoundingClientRect()
      let top = rect.bottom + 8
      let left = rect.left - PANEL_W / 2

      top = Math.min(Math.max(top, 8), window.innerHeight - PANEL_H - 8)
      left = Math.min(Math.max(left, 8), window.innerWidth - PANEL_W - 8)

      const offset = (openCount % 5) * 40
      pos.value = { top: top + offset, left: left + offset }
      openCount += 1
      bringToFront()
      isVisible.value = true
      loadData()
    }

    const closePopover = () => {
      isVisible.value = false
      displayRows.value = []
      isFetching.value = false
      openCount = Math.max(0, openCount - 1)
    }

    // ── Portal: move panel to <body> so it escapes all stacking contexts ──
    onMounted(() => {
      if (panelRef.value) {
        document.body.appendChild(panelRef.value)
      }
    })

    onUnmounted(() => {
      document.removeEventListener('mousemove', onDragMove)
      document.removeEventListener('mouseup', onDragEnd)
      if (panelRef.value && document.body.contains(panelRef.value)) {
        document.body.removeChild(panelRef.value)
      }
    })

    return {
      triggerId,
      panelRef,
      isVisible,
      isFetching,
      panelStyle,
      columnDefs,
      shownRows,
      bringToFront,
      openPopover,
      closePopover,
      onHeaderMousedown
    }
  }
})
</script>

<style>
.bdx-pv-panel {
  position: fixed;
  width: 720px;
  max-width: calc(100vw - 32px);
  background: #fff;
  border: 2px solid #b8cfe0;
  border-radius: 6px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  overflow: visible;
}

.bdx-pv-scroll {
  max-height: 300px;
  overflow-y: auto;
  overflow-x: auto;
}

.bdx-pv-table .bdx-pv-th {
  background: #f8f9fa !important;
  border-bottom: 2px solid #dee2e6 !important;
  padding: 8px 14px 6px !important;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 1;
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em !important;
  color: #6c757d !important;
}

.bdx-pv-table td {
  white-space: nowrap;
  padding: 3px 8px !important;
}

.bdx-pv-drag-handle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-top: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #dee2e6;
  cursor: grab;
  user-select: none;
}
.bdx-pv-drag-handle:active { cursor: grabbing; }

.bdx-pv-grip {
  font-size: 1.1rem;
  color: #adb5bd;
  line-height: 1;
  pointer-events: none;
  flex-shrink: 0;
  letter-spacing: -1px;
}

.bdx-pv-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #212529;
  pointer-events: auto;
  cursor: text;
  user-select: text;
}

.bdx-pv-close-btn {
  background: none;
  border: none;
  outline: none;
  padding: 2px 7px;
  font-size: 1.2rem;
  line-height: 1;
  color: #6c757d;
  cursor: pointer;
  border-radius: 3px;
  transition: background 0.12s, color 0.12s;
}
.bdx-pv-close-btn:focus { outline: none; box-shadow: none; }
.bdx-pv-close-btn:hover { background: #fee2e2; color: #dc3545; }
</style>

<style scoped>
.bdx-preview-wrapper {
  display: contents;
}
</style>
